import {createContext, useContext, useState} from "react";
import {RemoteConstantsModel} from "./models/remoteConstants.model";
import {CommandBase} from "./commands/commandBase";
import {get} from "./utils/http.client";
import {apiBindings} from "./utils/constants";

interface IntegratorProviderProps {
    children: React.ReactNode;
}

interface IntegratorContextData {
    branches: BranchData[];
    setBranches(value: BranchData[]): void;
    branch: string;
    setBranch(value: string): void;
    applyBranch(value: string): Promise<boolean>;
    state: RemoteConstantsModel;
    totalChanges: number;
    getBranchVersionNumber(branch: string): number;
    forceSetState(state: RemoteConstantsModel): void;
    refreshState(branch: string): Promise<boolean>;
    refreshBranches(): Promise<boolean>;
    executeCommand(command: CommandBase): void;
    clearHistory(): void;
    goBack(): void;
    goForward(): void;
    canGoBack(): boolean;
    canGoForward(): boolean;
}

interface HistoryData {
    frame: number;
    data: string;
    command: CommandBase | null;
}

interface BranchData {
    name: string;
    versionNumber: number;
}

// One-time migration: unify all metadata to uid-based identity.
// - Instance names: localization keyed by uid
// - Description templates: localization keyed by id (shared per type)
// - Entity attribute arrays: store uids instead of ids
// TODO: Remove this once all branches have been loaded and saved.
function migrateToUidBased(state: RemoteConstantsModel) {
    if (!state.localization) return;

    const allAttributes = [
        ...(state.unitAttributes ?? []),
        ...(state.buildingAttributes ?? []),
        ...(state.techActionAttributes ?? []),
        ...(state.techPassiveAttributes ?? []),
        ...(state.rewards ?? []),
    ];

    // Ensure uid-keyed localization exists for instance names
    for (const attr of allAttributes) {
        if (attr.uid === attr.id) continue;
        for (const lang of Object.keys(state.localization)) {
            const loc = state.localization[lang];
            // If uid entry missing but id entry exists, copy id→uid for the name
            if (loc[attr.uid] === undefined && loc[attr.id] !== undefined) {
                loc[attr.uid] = loc[attr.id];
            }
        }
    }

    // Convert unit/building attribute arrays from id to uid
    const attrIdToUid = new Map<string, string>();
    for (const attr of [...(state.unitAttributes ?? []), ...(state.buildingAttributes ?? [])]) {
        // Only map if id is unique (safe to migrate). If duplicates exist, skip (already uid-based or manual fix needed).
        if (!attrIdToUid.has(attr.id)) {
            attrIdToUid.set(attr.id, attr.uid);
        } else {
            attrIdToUid.delete(attr.id); // ambiguous — don't auto-migrate
        }
    }

    for (const unit of (state.units ?? [])) {
        if (!unit.attributes) continue;
        unit.attributes = unit.attributes.map(a => attrIdToUid.get(a) ?? a);
    }
    for (const building of (state.buildings ?? [])) {
        if (!building.attributes) continue;
        building.attributes = building.attributes.map(a => attrIdToUid.get(a) ?? a);
    }
    // Building rewards already use uids — no change needed
}

// One-time migration: convert the flat per-building reward fields
// (collectionTrigger/Type/Requirement, goldPerTurn, populationOnCreation,
// turnsUntilFirstTrigger, amountIncreasePerTrigger, triggerCadence) into
// the new inline `passiveRewards: BuildingReward[]` array. Synthesizes at
// most one reward per building from whatever flat config was present.
// TODO: Remove this once all branches have been loaded and saved.
function migrateBuildingPassiveRewards(state: RemoteConstantsModel) {
    for (const building of (state.buildings ?? [])) {
        const anyB = building as any;
        // Already migrated or freshly created with the new shape.
        if (Array.isArray(building.passiveRewards)) {
            // Still clean up any old fields that may have lingered.
        } else {
            building.passiveRewards = [];
        }

        const trigger = anyB.collectionTrigger as string | undefined;
        const type = anyB.collectionType as string | undefined;
        const requirement = anyB.collectionRequirement as string | undefined;
        const gold = anyB.goldPerTurn as number | undefined;
        const pop = anyB.populationOnCreation as number | undefined;
        const delay = anyB.turnsUntilFirstTrigger as number | undefined;
        const increase = anyB.amountIncreasePerTrigger as number | undefined;
        const cadence = (building.properties as any)?.triggerCadence as number | undefined;

        const grantKind: 'Gold' | 'Population' | undefined =
            type === 'Gold' ? 'Gold' : type === 'Population' ? 'Population' : undefined;
        const amount = grantKind === 'Gold' ? (gold ?? 0) : grantKind === 'Population' ? (pop ?? 0) : 0;

        const shouldSynthesize = building.passiveRewards.length === 0
            && grantKind !== undefined && trigger !== undefined && trigger !== 'None';

        if (shouldSynthesize) {
            let triggerSpec: any;
            if (trigger === 'OnPlace') {
                triggerSpec = {kind: 'OnPlace'};
            } else if (requirement === 'FullTurnOccupied') {
                triggerSpec = {kind: 'FullTurnOccupied'};
            } else if (trigger === 'TilesExplored') {
                triggerSpec = {kind: 'TilesExplored', cloudCount: requirement === 'CloudCount' ? Math.max(1, cadence ?? 1) : 1};
            } else {
                // Legacy TurnStart / TurnEnd trigger names both map to EveryNTurns.
                // Phase has been removed; periodic rewards always fire on turn start.
                triggerSpec = {
                    kind: 'EveryNTurns',
                    cadence: cadence ?? 1,
                    firstTriggerDelay: delay !== undefined && delay >= 0 ? delay : 0,
                };
            }

            building.passiveRewards.push({
                uid: crypto.randomUUID(),
                active: true,
                trigger: triggerSpec,
                grant: {kind: grantKind!, amount},
                amountIncreasePerTrigger: increase ?? 0,
            });
        }

        // Strip flat fields regardless — they no longer exist in the new shape.
        delete anyB.collectionTrigger;
        delete anyB.collectionType;
        delete anyB.collectionRequirement;
        delete anyB.goldPerTurn;
        delete anyB.populationOnCreation;
        delete anyB.turnsUntilFirstTrigger;
        delete anyB.amountIncreasePerTrigger;
        if (building.properties) {
            delete (building.properties as any).triggerCadence;
        }
    }
}

// One-time migration: buildings start at Level 1 in the runtime, so the
// data structures indexed by level (`rewards[level]`, `imagePacks[id][level]`)
// never use index 0. Make sure every building has at least a dead slot at
// index 0 so the editor's "skip level 0" rendering doesn't leave the building
// looking empty. Idempotent — safe to run on already-migrated data.
function migrateBuildingLevelZeroSlots(state: RemoteConstantsModel) {
    for (const building of (state.buildings ?? [])) {
        if (!Array.isArray(building.rewards) || building.rewards.length === 0) {
            building.rewards = [[]];
        }
        const pack = state.imagePacks?.[building.id];
        if (pack && pack.length < 2) {
            // Seed Level 1 with a copy of whatever Level 0 had so the editor
            // shows the default placeholder under "Level 1".
            pack.push([...(pack[0] ?? [building.id])]);
        }
    }
}

const IntegratorContext = createContext<IntegratorContextData>({} as IntegratorContextData);

export function IntegratorProvider(props: IntegratorProviderProps) {
    const [branches, setBranches] = useState<BranchData[]>([]);
    const [branch, setBranch] = useState<string>(getStoredBranch());
    const [frame, setFrame] = useState<number>(0);
    const [state, setState] = useState<RemoteConstantsModel>({} as RemoteConstantsModel);
    const [history, setHistory] = useState<HistoryData[]>([]);
    const [historyIndex, setHistoryIndex] = useState<number>(0);

    function executeCommand(command: CommandBase): void {
        const result = command.execute(state);
        if (!result) {
            return;
        }

        const newFrame = frame + 1;
        setFrame(newFrame);

        if (historyIndex > 0) {
            setHistoryIndex(0);
            history.splice(0, historyIndex);
        }

        history.unshift({
            frame: newFrame,
            data: JSON.stringify(state),
            command: command
        });

        setHistory(history);
        forceSetState(state);
    }

    function goBack() {
        if (!canGoBack()) {
            return;
        }

        setFromHistory(historyIndex + 1);
    }

    function goForward() {
        if (!canGoForward()) {
            return;
        }

        setFromHistory(historyIndex - 1);
    }

    function canGoBack(): boolean {
        return history.length - historyIndex - 1 > 0;
    }

    function canGoForward(): boolean {
        return historyIndex > 0;
    }

    function clearHistory(): void {
        applyHistory(state);
    }

    function setFromHistory(index: number) {
        const historyData = history[index];
        setFrame(historyData.frame);
        setHistoryIndex(index);
        setState(JSON.parse(historyData.data));
    }

    async function refreshState(branch: string): Promise<boolean> {
        const dataResult = await get(`${apiBindings.api}getRemoteConstants?branch=${branch}`, undefined);
        if (!dataResult.success) {
            return false;
        }

        const newData = dataResult.data as RemoteConstantsModel;
        migrateToUidBased(newData);
        migrateBuildingPassiveRewards(newData);
        migrateBuildingLevelZeroSlots(newData);
        setState(newData);
        applyHistory(newData);

        return true;
    }

    function applyHistory(state: RemoteConstantsModel) {
        setFrame(0);
        setHistoryIndex(0);
        setHistory([{
            frame: 0,
            data: JSON.stringify(state),
            command: null
        }]);
    }

    async function refreshBranches(): Promise<boolean> {
        const dataResult = await get(`${apiBindings.api}getAllBranches`, undefined);
        if (!dataResult.success) {
            return false;
        }

        setBranches(dataResult.data);
        return true;
    }

    function forceSetState(state: RemoteConstantsModel) {
        setState(() => ({
            ...state
        }));
    }

    async function applyBranch(branch: string): Promise<boolean> {
        const result = await refreshState(branch);
        if (!result) {
            return false;
        }

        setBranch(branch);
        storeBranch(branch);
        return true;
    }

    function getBranchVersionNumber(branch: string): number {
        return branches.find(b => b.name === branch)?.versionNumber ?? -1;
    }

    function getStoredBranch(): string {
        return localStorage.getItem('rift-branch') ?? 'Master';
    }

    function storeBranch(branch: string) {
        localStorage.setItem('rift-branch', branch);
    }

    return (
        <IntegratorContext.Provider
            value={{
                branches, setBranches,
                branch, setBranch,
                applyBranch,
                state, forceSetState,
                totalChanges: history.length - historyIndex - 1,
                getBranchVersionNumber,
                refreshState,
                refreshBranches,
                executeCommand,
                clearHistory,
                goBack,
                goForward,
                canGoBack,
                canGoForward,
            }}>
            {props.children}
        </IntegratorContext.Provider>
    )
}

export const useIntegrator = () => useContext(IntegratorContext);